/**
 * Task Priority Suggestion Workflow
 *
 * This workflow provides intelligent task priority suggestions:
 * 1. Detects new task creation
 * 2. Analyzes task context (deadline, project health, dependencies)
 * 3. Suggests priority level based on business rules
 * 4. Notifies user with priority recommendation
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const taskPrioritySuggestionWorkflow: Workflow = {
  id: 'task-priority-suggestion',
  name: 'Suggerimento Priorità Task',
  description: 'Analizza automaticamente i nuovi task e suggerisce il livello di priorità appropriato basandosi su scadenze, contesto del progetto e dipendenze',
  trigger: {
    type: 'event',
    config: {
      // Trigger when a new task is created
      eventType: 'entity.created',
      entityType: 'task',
      // Only trigger if priority is not already set
      conditions: {
        field: 'priority',
        operator: 'not_exists',
      },
    },
  },
  steps: [
    {
      id: 'analyze_context',
      name: 'Analizza Contesto',
      type: 'ai_analyze',
      config: {
        prompt: `Analizza questo nuovo task e il suo contesto per suggerire la priorità appropriata.

Considera:
- Scadenza del task (quanto è vicina?)
- Stato del progetto correlato (in ritardo? critico?)
- Task dipendenti (ci sono altri task bloccati da questo?)
- Descrizione del task (contiene parole come "urgente", "importante"?)
- Carico di lavoro attuale dell'assignee

Rispondi in formato JSON:
{
  "suggestedPriority": "low" | "medium" | "high",
  "reasoning": "spiegazione breve del perché",
  "deadlineProximity": "immediate" | "near" | "far" | "none",
  "projectCriticality": "low" | "medium" | "high",
  "hasDependentTasks": boolean
}`,
        includeTaskContext: true,
        includeProjectContext: true,
      },
      next: ['suggest_priority'],
    },
    {
      id: 'suggest_priority',
      name: 'Genera Suggerimento',
      type: 'custom',
      config: {
        // Create a priority suggestion record
        action: 'create_priority_suggestion',
        storeInTaskMetadata: true,
        // Don't auto-apply, let user confirm
        autoApply: false,
      },
      next: ['send_notification'],
    },
    {
      id: 'send_notification',
      name: 'Notifica Suggerimento',
      type: 'send_notification',
      config: {
        type: 'in_app',
        message: 'Ho analizzato il task "{taskTitle}" e suggerisco priorità {suggestedPriority}. Motivo: {reasoning}',
        includeTaskLink: true,
        action: {
          label: 'Applica Suggerimento',
          handler: 'apply_priority_suggestion',
        },
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Calculate priority based on deadline proximity
 */
export function calculatePriorityFromDeadline(
  deadline: string | null,
  currentDate: Date = new Date()
): 'low' | 'medium' | 'high' {
  if (!deadline) return 'medium';

  const deadlineDate = new Date(deadline);
  const daysUntilDeadline = Math.ceil(
    (deadlineDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  if (daysUntilDeadline <= 2) return 'high';
  if (daysUntilDeadline <= 7) return 'medium';
  return 'low';
}

/**
 * Extract urgency indicators from task description
 */
export function extractUrgencyFromDescription(description: string): boolean {
  const urgentKeywords = [
    'urgente',
    'immediato',
    'critico',
    'asap',
    'subito',
    'prioritario',
    'importante',
  ];

  const lowerDesc = description.toLowerCase();
  return urgentKeywords.some(keyword => lowerDesc.includes(keyword));
}
